import { useEffect } from 'react';
import { t } from '../theme';
import { useStore } from '../store';

/**
 * A panel showing stale data without saying so is worse than one that admits
 * it — you would trust "noch 3 Tage" on a fridge item that was eaten
 * yesterday. Reads keep working from the service worker cache; writes are
 * refused rather than silently queued.
 */
export function OfflineBanner() {
  const online = useStore((s) => s.online);
  const lastSyncAt = useStore((s) => s.lastSyncAt);
  const setOnline = useStore((s) => s.setOnline);
  const bootstrap = useStore((s) => s.bootstrap);

  useEffect(() => {
    let cancelled = false;
    let wasOnline = useStore.getState().online;

    /**
     * navigator.onLine only knows whether the *device* has a link — it stays
     * true when the WLAN is fine but the VPS is unreachable, which is the more
     * likely failure for a wall panel. And because the service worker serves
     * cached API responses transparently, a successful fetch proves nothing
     * either. /api/health is deliberately excluded from runtime caching, so it
     * is the one request that still reflects reality.
     */
    const probe = async () => {
      let reachable = false;
      if (navigator.onLine) {
        try {
          const res = await fetch('/api/health', { cache: 'no-store', signal: AbortSignal.timeout(5000) });
          reachable = res.ok;
        } catch {
          reachable = false;
        }
      }
      if (cancelled) return;
      setOnline(reachable);
      // Only refetch on the transition, not on every successful probe.
      if (reachable && !wasOnline) void bootstrap();
      wasOnline = reachable;
    };

    void probe();
    const timer = setInterval(() => void probe(), 30_000);
    const onChange = () => void probe();

    /**
     * iOS freezes a backgrounded PWA, which kills the SSE connection without
     * firing anything the page can see. Coming back to the foreground is the
     * only reliable moment to notice, so refetch there rather than trusting
     * the stream to have survived.
     */
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void probe();
      void useStore.getState().bootstrap();
    };

    window.addEventListener('online', onChange);
    window.addEventListener('offline', onChange);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('online', onChange);
      window.removeEventListener('offline', onChange);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [setOnline, bootstrap]);

  if (online) return null;

  const stamp = lastSyncAt
    ? new Date(lastSyncAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 18,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 22px',
        borderRadius: 999,
        background: 'oklch(0.3 0.06 60 / 0.94)',
        border: '1px solid oklch(0.78 0.14 70 / 0.5)',
        backdropFilter: 'blur(14px)',
        fontSize: 18,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        zIndex: 45,
        pointerEvents: 'none',
      }}
    >
      <div style={{ width: 10, height: 10, borderRadius: 999, background: 'oklch(0.8 0.15 70)' }} />
      {stamp ? `Offline — Stand von ${stamp} Uhr` : 'Offline'}
      <span style={{ fontSize: 16, color: t.inkMuted }}>Änderungen erst wieder online</span>
    </div>
  );
}
